import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function SiniiLogo() {
  return (
    <svg viewBox="0 0 220 60" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-[220px]">
      <text
        x="0" y="52"
        fontFamily="Inter, Arial Black, sans-serif"
        fontSize="58"
        fontWeight="800"
        fill="#3fb5d2"
        letterSpacing="-3"
      >SINII</text>
      {/* Star accent */}
      <g transform="translate(196, 2) scale(0.85)">
        <path
          d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"
          fill="#3fb5d2"
        />
      </g>
    </svg>
  );
}

function DashboardMockup() {
  return (
    <div className="w-full h-full bg-gradient-to-br from-[#f0fafb] to-[#e0f4f8] flex flex-col gap-3 p-5">
      {/* Header bar mockup */}
      <div className="flex items-center gap-2">
        <div className="w-16 h-4 bg-[#3fb5d2] rounded-full opacity-80" />
        <div className="flex-1 h-4 bg-[#3fb5d2] rounded-full opacity-20" />
        <div className="w-8 h-8 bg-[#3fb5d2] rounded-full opacity-40" />
      </div>
      {/* Stat cards mockup */}
      <div className="grid grid-cols-3 gap-2 mt-1">
        {[80, 60, 90].map((h, i) => (
          <div key={i} className="bg-white rounded-xl p-3 shadow-sm">
            <div className="w-8 h-2 bg-[#3fb5d2] rounded opacity-40 mb-2" />
            <div className="w-full h-5 bg-[#3fb5d2] rounded opacity-70" style={{ width: `${h}%` }} />
          </div>
        ))}
      </div>
      {/* Chart mockup */}
      <div className="bg-white rounded-xl p-3 shadow-sm flex-1">
        <div className="w-20 h-2 bg-[#3fb5d2] rounded opacity-40 mb-3" />
        <div className="flex items-end gap-1 h-16">
          {[40, 65, 45, 80, 55, 70, 50, 85, 60, 75].map((h, i) => (
            <div key={i} className="flex-1 bg-[#3fb5d2] rounded-sm opacity-60" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      {/* Table mockup */}
      <div className="bg-white rounded-xl p-3 shadow-sm">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-2 items-center py-1 border-b border-gray-50 last:border-0">
            <div className="w-5 h-5 bg-[#3fb5d2] rounded-full opacity-40" />
            <div className="flex-1 h-2 bg-gray-200 rounded" />
            <div className="w-10 h-2 bg-[#3fb5d2] rounded opacity-50" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EyeIcon({ open }) {
  if (open) {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </svg>
    );
  }
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isSignUp) {
        await signUp(email, password, fullName, 'hunter');
        alert('Account created! Please check your email to verify your account.');
      } else {
        await signIn(email, password);
        navigate('/');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "h-10 border border-[#e5e5e5] rounded-lg px-4 text-sm text-[#3b3b3b] placeholder-[#8a8a8a] focus:outline-none focus:border-[#3fb5d2] focus:ring-1 focus:ring-[#3fb5d2] w-full transition-colors";

  return (
    <div className="min-h-screen flex bg-[#2a798c]">
      {/* Left Panel */}
      <div className="hidden lg:flex flex-1 items-center p-8">
        <div
          className="bg-white border border-[#3fb5d2] rounded-3xl w-full relative overflow-hidden flex flex-col"
          style={{ height: 'calc(100vh - 64px)', minHeight: '640px', boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' }}
        >
          {/* Logo area */}
          <div className="px-[71px] pt-[71px]">
            <SiniiLogo />
            <p className="text-[#3fb5d2] font-semibold text-[28px] leading-9 tracking-[-0.005px] mt-2">
              DASHBOARD
            </p>
          </div>

          {/* Dashboard mockup */}
          <div
            className="absolute overflow-hidden"
            style={{
              top: '316px',
              left: '104px',
              right: '-6px',
              bottom: 0,
              border: '6px solid #2a798c',
              borderRadius: '24px',
            }}
          >
            <DashboardMockup />
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex items-center justify-center w-full lg:w-[45%] xl:w-[40%] p-4 sm:p-6 min-w-0">
        <div className="bg-white rounded-2xl p-5 sm:p-6 w-full max-w-[443px] flex flex-col gap-6">
          {/* Header */}
          <div className="flex flex-col gap-2">
            <h1 className="text-[#3b3b3b] text-[28px] sm:text-[36px] font-semibold leading-tight sm:leading-10 tracking-[-0.008px]">
              {isSignUp ? 'Register' : 'Login'}
            </h1>
            <p className="text-[#646464] text-sm leading-5">
              {isSignUp ? 'Create your account' : 'Enter your information'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {isSignUp && (
              <div className="flex flex-col gap-[5px]">
                <label className="text-[#3b3b3b] text-sm font-medium leading-5">Full Name</label>
                <input
                  type="text"
                  required={isSignUp}
                  placeholder="Enter Full Name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className={inputClass}
                />
              </div>
            )}

            <div className="flex flex-col gap-[5px]">
              <label className="text-[#3b3b3b] text-sm font-medium leading-5">Email</label>
              <input
                type="email"
                required
                placeholder="Enter Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                className={inputClass}
              />
            </div>

            <div className="flex flex-col gap-[5px]">
              <label className="text-[#3b3b3b] text-sm font-medium leading-5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  className={`${inputClass} pr-10`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a8a8a] hover:text-[#3b3b3b] transition-colors"
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="h-11 bg-[#3fb5d2] hover:bg-[#2a798c] text-white font-semibold text-base rounded-lg w-full transition-colors disabled:opacity-50 mt-2"
            >
              {loading ? 'Processing...' : isSignUp ? 'Register' : 'Login'}
            </button>
          </form>

          {/* Toggle sign up / login */}
          <p className="text-[#8a8a8a] text-sm text-center leading-5">
            {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
            <button
              type="button"
              onClick={() => { setIsSignUp(!isSignUp); setError(''); }}
              className="text-[#3fb5d2] font-medium hover:text-[#2a798c] transition-colors"
            >
              {isSignUp ? 'Login here' : 'Register here'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
